/**
 * World-space volume selection: sphere, box and the sphere-brush capsule path.
 *
 * Shapes are given as a world matrix mapping the unit shape to world space
 * (unit sphere of diameter 1, unit cube of side 1), matching SuperSplat, so a
 * gizmo-driven shape with any position/rotation/non-uniform scale works.
 * `footprint` > 0 grows the test by each splat's world radius times footprint.
 */
import type { SplatEditModel } from '../SplatEditModel';
import type { ScreenContext, ScreenRegion } from './screen';

/** Invert a column-major affine 4x4 (returns null when singular). */
export function invertAffine(m: ArrayLike<number>): Float32Array | null {
    const a00 = m[0], a01 = m[1], a02 = m[2];
    const a10 = m[4], a11 = m[5], a12 = m[6];
    const a20 = m[8], a21 = m[9], a22 = m[10];
    const b01 = a22 * a11 - a12 * a21;
    const b11 = -a22 * a10 + a12 * a20;
    const b21 = a21 * a10 - a11 * a20;
    const det = a00 * b01 + a01 * b11 + a02 * b21;
    if (Math.abs(det) < 1e-20) return null;
    const id = 1 / det;
    const r = new Float32Array(16);
    r[0] = b01 * id;
    r[1] = (-a22 * a01 + a02 * a21) * id;
    r[2] = (a12 * a01 - a02 * a11) * id;
    r[4] = b11 * id;
    r[5] = (a22 * a00 - a02 * a20) * id;
    r[6] = (-a12 * a00 + a02 * a10) * id;
    r[8] = b21 * id;
    r[9] = (-a21 * a00 + a01 * a20) * id;
    r[10] = (a11 * a00 - a01 * a10) * id;
    const tx = m[12], ty = m[13], tz = m[14];
    r[12] = -(r[0] * tx + r[4] * ty + r[8] * tz);
    r[13] = -(r[1] * tx + r[5] * ty + r[9] * tz);
    r[14] = -(r[2] * tx + r[6] * ty + r[10] * tz);
    r[15] = 1;
    return r;
}

/** Smallest scale factor of an affine matrix's linear part (world → unit). */
function minAxisScale(m: ArrayLike<number>): number {
    return Math.min(
        Math.hypot(m[0], m[1], m[2]),
        Math.hypot(m[4], m[5], m[6]),
        Math.hypot(m[8], m[9], m[10]),
    );
}

export function selectSphere(model: SplatEditModel, shapeToWorld: ArrayLike<number>, footprint = 0): Uint8Array {
    const n = model.numSplats;
    const result = new Uint8Array(n);
    const inv = invertAffine(shapeToWorld);
    if (!inv) return result;
    const centers = model.getWorldCenters();
    const radii = footprint > 0 ? model.getWorldRadii() : null;
    // world radius → unit-space radius (conservative for non-uniform shapes)
    const unitPerWorld = footprint > 0 ? 1 / Math.max(minAxisScale(shapeToWorld), 1e-12) : 0;
    const state = model.state;
    for (let i = 0; i < n; i++) {
        if (state[i] & 6) continue;
        const x = centers[i * 3], y = centers[i * 3 + 1], z = centers[i * 3 + 2];
        const ux = inv[0] * x + inv[4] * y + inv[8] * z + inv[12];
        const uy = inv[1] * x + inv[5] * y + inv[9] * z + inv[13];
        const uz = inv[2] * x + inv[6] * y + inv[10] * z + inv[14];
        const r = 0.5 + (radii ? radii[i] * footprint * unitPerWorld : 0);
        if (ux * ux + uy * uy + uz * uz <= r * r) result[i] = 1;
    }
    return result;
}

export function selectBox(model: SplatEditModel, shapeToWorld: ArrayLike<number>, footprint = 0): Uint8Array {
    const n = model.numSplats;
    const result = new Uint8Array(n);
    const inv = invertAffine(shapeToWorld);
    if (!inv) return result;
    const centers = model.getWorldCenters();
    const radii = footprint > 0 ? model.getWorldRadii() : null;
    const unitPerWorld = footprint > 0 ? 1 / Math.max(minAxisScale(shapeToWorld), 1e-12) : 0;
    const state = model.state;
    for (let i = 0; i < n; i++) {
        if (state[i] & 6) continue;
        const x = centers[i * 3], y = centers[i * 3 + 1], z = centers[i * 3 + 2];
        const ux = inv[0] * x + inv[4] * y + inv[8] * z + inv[12];
        const uy = inv[1] * x + inv[5] * y + inv[9] * z + inv[13];
        const uz = inv[2] * x + inv[6] * y + inv[10] * z + inv[14];
        const h = 0.5 + (radii ? radii[i] * footprint * unitPerWorld : 0);
        if (Math.abs(ux) <= h && Math.abs(uy) <= h && Math.abs(uz) <= h) result[i] = 1;
    }
    return result;
}

/**
 * Sphere brush: `path` is a flat list of (x, y, z, r) world-space samples. A
 * negative r starts a new sub-path (|r| is still the radius). A splat is hit
 * when its center is within the radius of a sample or of the capsule between
 * consecutive samples of one sub-path. When `mask` is given (footprint 0),
 * hits are limited to centers that also project inside the brush stroke, as
 * in SuperSplat, so the brush can't reach through to splats hidden behind.
 */
export function selectCapsulePath(
    model: SplatEditModel,
    path: ArrayLike<number>,
    options: { footprint?: number; screen?: ScreenContext; mask?: ScreenRegion } = {},
): Uint8Array {
    const n = model.numSplats;
    const result = new Uint8Array(n);
    const count = Math.floor(path.length / 4);
    if (count === 0) return result;

    // segments: start point, end point, radii
    type Seg = { ax: number; ay: number; az: number; bx: number; by: number; bz: number; ra: number; rb: number };
    const segs: Seg[] = [];
    let minX = Infinity, minY = Infinity, minZ = Infinity, maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    for (let k = 0; k < count; k++) {
        const x = path[k * 4], y = path[k * 4 + 1], z = path[k * 4 + 2], rr = path[k * 4 + 3];
        const r = Math.abs(rr);
        const startsPath = k === 0 || rr < 0;
        if (startsPath) {
            segs.push({ ax: x, ay: y, az: z, bx: x, by: y, bz: z, ra: r, rb: r });
        } else {
            const p = k - 1;
            segs.push({ ax: path[p * 4], ay: path[p * 4 + 1], az: path[p * 4 + 2], bx: x, by: y, bz: z, ra: Math.abs(path[p * 4 + 3]), rb: r });
        }
        minX = Math.min(minX, x - r); maxX = Math.max(maxX, x + r);
        minY = Math.min(minY, y - r); maxY = Math.max(maxY, y + r);
        minZ = Math.min(minZ, z - r); maxZ = Math.max(maxZ, z + r);
    }

    const footprint = options.footprint ?? 0;
    const centers = model.getWorldCenters();
    const radii = footprint > 0 ? model.getWorldRadii() : null;
    const maxGrow = radii ? maxOf(radii) * footprint : 0;
    minX -= maxGrow; minY -= maxGrow; minZ -= maxGrow; maxX += maxGrow; maxY += maxGrow; maxZ += maxGrow;

    const screen = footprint === 0 ? options.screen : undefined;
    const mask = footprint === 0 ? options.mask : undefined;
    const vp = screen?.viewProj;
    const state = model.state;

    for (let i = 0; i < n; i++) {
        if (state[i] & 6) continue;
        const x = centers[i * 3], y = centers[i * 3 + 1], z = centers[i * 3 + 2];
        if (x < minX || x > maxX || y < minY || y > maxY || z < minZ || z > maxZ) continue;
        const grow = radii ? radii[i] * footprint : 0;
        let hit = false;
        for (let s = 0; s < segs.length && !hit; s++) {
            const g = segs[s];
            const dx = g.bx - g.ax, dy = g.by - g.ay, dz = g.bz - g.az;
            const len2 = dx * dx + dy * dy + dz * dz;
            let t = len2 > 0 ? ((x - g.ax) * dx + (y - g.ay) * dy + (z - g.az) * dz) / len2 : 0;
            t = t < 0 ? 0 : (t > 1 ? 1 : t);
            const px = g.ax + dx * t - x, py = g.ay + dy * t - y, pz = g.az + dz * t - z;
            const r = g.ra + (g.rb - g.ra) * t + grow;
            if (px * px + py * py + pz * pz <= r * r) hit = true;
        }
        if (!hit) continue;
        if (vp && mask) {
            const w = vp[3] * x + vp[7] * y + vp[11] * z + vp[15];
            if (w <= 0) continue;
            const sx = ((vp[0] * x + vp[4] * y + vp[8] * z + vp[12]) / w) * 0.5 + 0.5;
            const sy = 0.5 - ((vp[1] * x + vp[5] * y + vp[9] * z + vp[13]) / w) * 0.5;
            if (!mask.contains(sx, sy)) continue;
        }
        result[i] = 1;
    }
    return result;
}

function maxOf(a: Float32Array): number {
    let m = 0;
    for (let i = 0; i < a.length; i++) if (a[i] > m) m = a[i];
    return m;
}
